import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  CircularProgress,
  Alert,
  Divider
} from '@mui/material';
import {
  Add as AddIcon
} from '@mui/icons-material';
import { downloadWebpage, getDownloadedWebpages, deleteDownloadedWebpage } from '../../services/api';
import { DownloadedWebpage } from '../../types/index';
import DownloadedWebpagesList from '../DownloadedWebpagesList';

interface WebpageDownloadTabProps {
  aiId: string;
  collectionId: string;
  onDownloadedWebpagesChange: (webpages: DownloadedWebpage[]) => void;
  onRefreshQnaPairs?: () => void;
}

const WebpageDownloadTab: React.FC<WebpageDownloadTabProps> = ({
  aiId,
  collectionId,
  onDownloadedWebpagesChange,
  onRefreshQnaPairs
}) => {
  const [url, setUrl] = useState('');
  const [downloading, setDownloading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [downloadedWebpages, setDownloadedWebpages] = useState<DownloadedWebpage[]>([]);

  // Define fetchDownloadedWebpages before it's used in useEffect
  const fetchDownloadedWebpages = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await getDownloadedWebpages(aiId, collectionId, { limit: 100 });
      const webpages = response.data.data || [];
      
      setDownloadedWebpages(webpages);
      onDownloadedWebpagesChange(webpages);
    } catch (err: any) {
      console.error('Error fetching downloaded webpages:', err);
      setError('Failed to load downloaded webpages: ' + (err.response?.data?.error || err.message));
    } finally {
      setLoading(false);
    }
  }, [aiId, collectionId, onDownloadedWebpagesChange]);

  // Load downloaded webpages on component mount
  useEffect(() => {
    fetchDownloadedWebpages();
  }, [aiId, collectionId, fetchDownloadedWebpages]);

  const handleDownloadUrl = async () => {
    if (!url.trim()) {
      setError('Please enter a URL');
      return;
    }

    try {
      // Normalize the URL
      let normalizedUrl = url.trim();
      if (!normalizedUrl.match(/^https?:\/\//i)) {
        normalizedUrl = 'https://' + normalizedUrl;
      }

      // Validate URL
      new URL(normalizedUrl);

      setDownloading(true);
      setError(null);
      setSuccess(null);

      console.log('Downloading URL:', normalizedUrl);

      const response = await downloadWebpage(aiId, collectionId, {
        url: normalizedUrl
      });

      console.log('Download response:', response.data);

      setSuccess(`Successfully downloaded: ${normalizedUrl}`);
      setUrl(''); // Clear the input

      // Refresh the list
      await fetchDownloadedWebpages();

      // Clear success message after 3 seconds
      setTimeout(() => {
        setSuccess(null);
      }, 3000);

    } catch (err: any) {
      console.error('Error downloading URL:', err);
      setError('Failed to download URL: ' + (err.response?.data?.error || err.message));
    } finally {
      setDownloading(false);
    }
  };

  const handleDeleteDownloadedWebpage = async (id: string) => {
    try {
      await deleteDownloadedWebpage(aiId, collectionId, id);
      
      // Update local state
      const updatedWebpages = downloadedWebpages.filter(webpage => webpage.id !== id);
      setDownloadedWebpages(updatedWebpages);
      onDownloadedWebpagesChange(updatedWebpages);
      
      setSuccess('Downloaded webpage deleted successfully');
      
      // Clear success message after 3 seconds
      setTimeout(() => {
        setSuccess(null);
      }, 3000);

    } catch (err: any) {
      console.error('Error deleting downloaded webpage:', err);
      setError('Failed to delete downloaded webpage: ' + (err.response?.data?.error || err.message));
    }
  };

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' && !downloading) {
      handleDownloadUrl();
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="body1" color="text.primary" sx={{ mb: 3 }}>
        Enter webpage URLs to download their content and images. You can then generate Q&A pairs from the downloaded content in the next step.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* URL Input Section */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 3 }}>
          <TextField
            label="Webpage URL"
            placeholder="https://example.com/page"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            onKeyPress={handleKeyPress}
            size="small"
            fullWidth
            disabled={downloading}
          />
        </Box>

        <Button
          variant="contained"
          color="primary"
          onClick={handleDownloadUrl}
          disabled={downloading || !url.trim()}
          startIcon={downloading ? <CircularProgress size={20} color="inherit" /> : <AddIcon />}
          sx={{ mb: 2 }}
        >
          {downloading ? 'Downloading...' : 'Download Webpage'}
        </Button>
        
        <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 1 }}>
          Images are automatically downloaded with webpage content
        </Typography>
      </Box>

      <Divider sx={{ mb: 3 }} />

      {/* Downloaded Webpages List */}
      <DownloadedWebpagesList
        downloadedWebpages={downloadedWebpages}
        selectedIds={[]} // No selection on download tab
        onSelectionChange={() => {}} // No selection functionality
        onDelete={handleDeleteDownloadedWebpage}
        onRefresh={fetchDownloadedWebpages}
        onRefreshQnaPairs={onRefreshQnaPairs}
        loading={loading}
        error={error}
        showSelection={false} // Hide selection checkboxes
      />
    </Box>
  );
};

export default WebpageDownloadTab;